// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { courseHandicap } from '../../utils/scoring';

import type { PlayerScpSource } from './types';

export type CourseHandicapInputs = {
  /** Official WHS Handicap Index (may be negative for plus handicaps). */
  handicapIndex: number;
  /** Tee slope rating (55–155 in WHS; must be > 0). */
  slopeRating: number;
  /** Tee course rating (must be > 0). */
  courseRating: number;
  /** Par for the tee/round. */
  par: number;
  /**
   * 9 or 18 — or 3 for a developer test round. Anything other than 9 is treated as a full
   * 18-hole round, so a 3-hole test round hands out the full 18-hole stroke allowance.
   */
  holesCount: number;
};

/**
 * WHS Course Handicap (= Playing Handicap at 100% allowance), taking the inputs as a
 * named object because a call site passing five bare numbers is a call site that can
 * swap slope and course rating without anyone noticing.
 *
 * The arithmetic itself is `courseHandicap` in utils/scoring, which mirrors
 * services/shared/Goffy.Scoring/CourseHandicap.cs. It used to live here, and only here
 * — so nothing on the server could derive a playing handicap, check one, or recompute
 * one after a tee change (CLAUDE.md §12).
 *
 * Game-specific handicap allowances (e.g. 95%) are applied downstream — this is
 * the canonical 100% course handicap that gets stored as the player's scp.
 */
export function calculateCourseHandicap(inputs: CourseHandicapInputs): number | null {
  const { handicapIndex, slopeRating, courseRating, par, holesCount } = inputs;
  return courseHandicap(handicapIndex, slopeRating, courseRating, par, holesCount);
}

export type ScpFillablePlayer = { id: string; scp: string; scpSource?: PlayerScpSource };
export type TeeRatings = { slopeRating: number; courseRating: number; par: number };

/**
 * Derives each player's playing handicap (scp) from their official handicap
 * index and the selected tee's ratings. Once a tee is known, the calculated WHS
 * course handicap is authoritative for every player with a handicap index, so
 * selecting or switching tee (or hole count) updates their scp — including one
 * prefilled from a prior round ('latest'), which is only the best guess until a
 * tee is picked. A value the user typed by hand ('manual') is never overwritten,
 * except that a cleared (blank) field is refilled — clearing is the way to ask
 * for the calculated value back. Players without a known handicap index, or
 * with incomplete tee data, are left as-is.
 *
 * Returns the SAME array reference when nothing changes, so callers can pass the
 * result straight to a state setter without triggering a needless re-render.
 */
export function updateCalculatedScps<T extends ScpFillablePlayer>(
  players: T[],
  handicapIndexByPlayerId: Record<string, number>,
  tee: TeeRatings | null | undefined,
  holesCount: number
): T[] {
  if (!tee) return players;

  let changed = false;
  const next = players.map((player) => {
    const isBlank = !player.scp.trim();
    if (!isBlank && player.scpSource === 'manual') return player;
    const handicapIndex = handicapIndexByPlayerId[player.id];
    if (handicapIndex === undefined) return player;
    const courseHandicap = calculateCourseHandicap({
      handicapIndex,
      slopeRating: tee.slopeRating,
      courseRating: tee.courseRating,
      par: tee.par,
      holesCount,
    });
    if (courseHandicap === null) return player;
    const nextScp = String(courseHandicap);
    if (player.scp === nextScp && player.scpSource === 'calculated') return player;
    changed = true;
    return { ...player, scp: nextScp, scpSource: 'calculated' as const };
  });

  return changed ? next : players;
}

/**
 * Parses a handicap index that may arrive as a number or a string (the player
 * service stores it as a decimal). Returns null when absent or unparseable.
 */
export function parseHandicapIndex(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}
